import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import BewaveFooter from '@/components/public/BewaveFooter'
import Breadcrumb from '@/components/public/Breadcrumb'
import CitySelect, { ALL_CITIES } from '@/components/public/CitySelect'
import ExploreCtaBanner from '@/components/public/ExploreCtaBanner'
import PublicHeader from '@/components/public/PublicHeader'
import SchoolCard from '@/components/public/SchoolCard'
import ServiceFilters from '@/components/public/ServiceFilters'
import {
  EXPLORE_NAME,
  EXPLORE_ALL_PATH,
  EXPLORE_PATH,
  PUBLIC_CONTAINER,
} from '@/components/public/publicShell'
import type {
  CityOption,
  DirectorySchool,
  ServiceFacet,
} from '@/types/publicSchool'

const ALL_PATH = EXPLORE_ALL_PATH

interface ExploreAllProps {
  listings: DirectorySchool[]
  cities: CityOption[]
  services: ServiceFacet[]
  query: string | null
  selected_city: string | null
  selected_category: string | null
  selected_service: string | null
  category_label: string | null
  category_headline: string | null
  total_count: number
  page: number
  total_pages: number
  per_page: number
}

/**
 * A listagem completa do Explore, em /explore/todos.
 *
 * É o destino do "Ver todos" das seções do diretório: lá a vitrine mostra no
 * máximo 12 por categoria, e sem esta página o botão não teria para onde ir.
 *
 * Sem mapa e sem recorte por raio de propósito — quem chega aqui já sabe que
 * quer ver tudo. O que sobra é grade, busca, filtro e paginação.
 */
export default function ExploreAll({
  listings,
  cities,
  services,
  query,
  selected_city,
  selected_category,
  selected_service,
  category_label,
  category_headline,
  total_count,
  page,
  total_pages,
}: ExploreAllProps) {
  const [term, setTerm] = useState(query ?? '')

  // A caixa de busca é controlada, mas o servidor manda a verdade: voltar pelo
  // histórico ou limpar o filtro precisa recolocar o texto certo no campo.
  useEffect(() => setTerm(query ?? ''), [query])

  const currentQuery = useMemo(
    () => ({
      q: query ?? undefined,
      city: selected_city ?? undefined,
      category: selected_category ?? undefined,
      service: selected_service ?? undefined,
    }),
    [query, selected_city, selected_category, selected_service],
  )

  const visit = useCallback(
    (
      params: Record<string, string | number | undefined>,
      options: { replace?: boolean; onFinish?: () => void } = {},
    ) => {
      const clean: Record<string, string | number> = {}
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') clean[key] = value
      })
      router.get(ALL_PATH, clean, {
        preserveState: true,
        preserveScroll: true,
        ...options,
      })
    },
    [],
  )

  // Trocar qualquer filtro volta para a primeira página: manter a 5 depois de
  // recortar mostraria uma grade vazia num resultado que tem conteúdo.
  const filtrar = useCallback(
    (
      patch: Record<string, string | undefined>,
      options?: { replace?: boolean },
    ) => visit({ ...currentQuery, ...patch, page: undefined }, options),
    [currentQuery, visit],
  )

  /**
   * Busca enquanto se digita.
   *
   * A espera de 350ms não é enfeite: cada busca é uma ida ao servidor, e sem ela
   * "maré cheia" viraria dez requisições, com as respostas podendo chegar fora
   * de ordem. `replace: true` no histórico pelo mesmo motivo — sem isso, voltar
   * uma vez andaria uma letra para trás.
   *
   * O termo já no ar não dispara nada: sem essa guarda, receber a resposta
   * (`query` muda) reagendaria a mesma busca, e ela ficaria em laço.
   */
  useEffect(() => {
    const limpo = term.trim()
    if (limpo === (query ?? '')) return

    const id = setTimeout(
      () => filtrar({ q: limpo || undefined }, { replace: true }),
      350,
    )
    return () => clearTimeout(id)
  }, [term, query, filtrar])

  const temFiltro = Boolean(
    query || selected_city || selected_category || selected_service,
  )

  /**
   * A página chegou recortada por uma categoria — o "Ver todos" de uma seção do
   * diretório.
   *
   * É a CATEGORIA e não o serviço que manda aqui, ainda que os dois recortem: o
   * serviço é escolhido nesta página, na própria fila de filtros, e esconder a
   * fila em cima da escolha faria o controle sumir no instante em que fosse
   * usado — sem jeito de trocar de serviço ou de voltar para "Todos". A
   * categoria vem pronta da tela anterior, e nada nesta página a define.
   */
  const escopada = Boolean(selected_category)

  /**
   * A grade ACUMULA as páginas em vez de trocá-las.
   *
   * A paginação numerada trocava a grade inteira e mandava a pessoa de volta ao
   * topo a cada clique. Aqui a página seguinte entra no fim do que já está na
   * tela, e o servidor continua paginando igual — só o cliente é que costura.
   *
   * A assinatura do recorte é o que separa "chegou a página 2" de "mudou o
   * filtro": no segundo caso a lista recomeça, senão a busca nova apareceria
   * grudada nos resultados da anterior.
   */
  const assinatura = JSON.stringify(currentQuery)
  const [acumulado, setAcumulado] = useState(() => ({
    assinatura,
    page,
    itens: listings,
  }))

  useEffect(() => {
    setAcumulado((anterior) => {
      if (anterior.assinatura !== assinatura) {
        return { assinatura, page, itens: listings }
      }
      if (page === anterior.page) return anterior

      // Filtra por id: voltar pelo histórico pode reentregar uma página que já
      // está na tela, e a chave repetida quebraria a grade.
      const vistos = new Set(anterior.itens.map((item) => item.id))
      return {
        assinatura,
        page,
        itens: [
          ...anterior.itens,
          ...listings.filter((item) => !vistos.has(item.id)),
        ],
      }
    })
  }, [assinatura, page, listings])

  const [carregando, setCarregando] = useState(false)
  const temMais = acumulado.page < total_pages

  const carregarMais = useCallback(() => {
    if (carregando || !temMais) return
    setCarregando(true)
    // `replace` no histórico: rolar a lista não é navegar, e sem isso o botão
    // "voltar" desfaria uma página de rolagem por vez em vez de sair da tela.
    visit(
      { ...currentQuery, page: acumulado.page + 1 },
      { replace: true, onFinish: () => setCarregando(false) },
    )
  }, [carregando, temMais, acumulado.page, currentQuery, visit])

  /**
   * Carrega ao chegar perto do fim.
   *
   * O observador mira o próprio botão "Carregar mais" — que continua ali,
   * clicável, para quem navega por teclado e para o caso de o observador não
   * disparar. A margem de 600px pede a página seguinte antes de a atual acabar,
   * então a rolagem não trava esperando resposta.
   */
  const sentinela = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const alvo = sentinela.current
    if (!alvo || !temMais) return

    const observador = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) carregarMais()
      },
      { rootMargin: '600px' },
    )
    observador.observe(alvo)
    return () => observador.disconnect()
  }, [temMais, carregarMais])

  const titulo = category_headline ?? `Tudo no ${EXPLORE_NAME}`

  return (
    <>
      <Head
        title={`${category_label ?? 'Todos os negócios'} — ${EXPLORE_NAME}`}
      />

      <div className="min-h-screen bg-bewave-surface text-foreground">
        {/* A fila de filtros só aparece na listagem SEM recorte.

            Chegando por "Ver todos" de uma categoria (ou por um serviço), a
            página já responde a uma pergunta escolhida — e a fila oferecia os 33
            serviços do catálogo inteiro, a maioria fora do recorte: com
            "Esportes" no ar, clicar em "Pousadas" zerava a lista sem explicar
            por quê. O caminho para trocar de recorte é o "Limpar filtros", que
            devolve a listagem aberta com a fila de volta. */}
        <PublicHeader
          brandHref="/"
          belowRow={
            escopada ? undefined : (
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
                <div className="flex shrink-0 items-center gap-2 lg:order-2">
                  <CitySelect
                    cities={cities}
                    selected={selected_city}
                    onChange={(city) =>
                      filtrar({ city: city === ALL_CITIES ? undefined : city })
                    }
                    className="shrink-0 rounded-full border bg-white shadow-sm"
                  />
                </div>

                <ServiceFilters
                  className="lg:order-1"
                  facets={services}
                  selected={selected_service}
                  onChange={(service) =>
                    filtrar({ service: service ?? undefined })
                  }
                />
              </div>
            )
          }
        />

        <div className={cn(PUBLIC_CONTAINER, 'py-8')}>
          <Breadcrumb
            items={[
              { label: 'Início', href: '/' },
              { label: 'Explorar', href: EXPLORE_PATH },
              { label: category_label ?? 'Todos' },
            ]}
          />

          <h1 className="mt-3 max-w-3xl text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
            {titulo}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            {total_count} {total_count === 1 ? 'negócio' : 'negócios'}
            {/* Quantos já estão na tela, para a rolagem ter fim visível. */}
            {temMais && ` · mostrando ${acumulado.itens.length}`}
          </p>

          {/* Busca pelo NOME do negócio, e só. Cidade e serviço já têm filtro
              próprio logo acima — um termo que também atravessasse os dois
              faria o resultado discordar dos controles.

              Busca ao digitar, com espera curta (ver o efeito acima). O botão
              e o enter continuam valendo: eles antecipam a busca que já ia
              acontecer e, no mobile, fecham o teclado — que é o que a pessoa
              espera de um campo com botão ao lado. */}
          <form
            className="mt-5 flex max-w-2xl items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              filtrar({ q: term.trim() || undefined }, { replace: true })
            }}
          >
            {/* `min-w-0` deixa o campo encolher quando os dois botões dividem a
                linha: sem ele o conteúdo mínimo do input empurraria "Limpar"
                para fora da tela no mobile. */}
            <div className="relative min-w-0 flex-1">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400"
              />
              {/* Os três controles da linha levam `h-11` na mão.
                  O padrão do design system é `h-9` nos dois, e medido eles já
                  batiam no pixel — mas o campo de contorno fino lê menor do que
                  a pílula sólida ao lado dele na mesma altura. Com mais corpo o
                  contorno ganha presença e a linha para de parecer desalinhada;
                  de quebra, 44px é alvo de toque de verdade no mobile, que 36
                  não era. */}
              <Input
                type="search"
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                placeholder="Busque pelo nome do negócio"
                aria-label="Buscar pelo nome do negócio"
                className="h-11 rounded-full bg-white pl-9"
              />
            </div>

            <Button type="submit" className="h-11 shrink-0 rounded-full px-6">
              Buscar
            </Button>

            {/* Ao lado do "Buscar", não embaixo: limpar é a ação irmã de buscar,
                e uma linha só para ela empurrava a grade para baixo cada vez que
                alguém filtrava. No mobile perde o "filtros" — o rótulo inteiro
                comeria a largura do campo. */}
            {temFiltro && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => router.get(ALL_PATH)}
                className="h-11 shrink-0 gap-1.5 rounded-full px-3 text-primary hover:bg-primary/5 hover:text-primary"
              >
                <X className="size-4" aria-hidden="true" />
                {/* Um filho de flex só: o "filtros" dentro do mesmo <span> que o
                    "Limpar", senão o `gap-1.5` do botão se soma ao espaço da
                    palavra e sai "Limpar  filtros". */}
                <span>
                  Limpar<span className="hidden sm:inline">{' filtros'}</span>
                </span>
              </Button>
            )}
          </form>

          {acumulado.itens.length === 0 ? (
            <p className="mt-10 text-base text-gray-600">
              Nada encontrado com esses filtros.
            </p>
          ) : (
            <div className="mt-6 grid gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {acumulado.itens.map((listing) => (
                <SchoolCard key={listing.id} school={listing} showServiceTag />
              ))}
            </div>
          )}

          {/* O fim da lista é também o gatilho: o observador acima mira esta
              div, e o botão dentro dela é a saída para quem chega por teclado —
              ou para quando o observador não dispara. */}
          {temMais && (
            <div ref={sentinela} className="mt-10 flex justify-center">
              <Button
                type="button"
                variant="outline"
                onClick={carregarMais}
                disabled={carregando}
                className="h-11 rounded-full px-8"
              >
                {carregando ? 'Carregando…' : 'Carregar mais'}
              </Button>
            </div>
          )}

          <ExploreCtaBanner className="mt-14" />
        </div>

        <BewaveFooter cities={cities} />
      </div>
    </>
  )
}
